import { useCallback, useState } from 'react';
import { useRouter } from 'next/router';
import { useDispatch } from 'react-redux';
import moment from 'moment-timezone';
import { setBookingDetails } from '../../lib/store/bookingSlice';
import LocationConfirmDialog from '../../components/modals/LocationConfirmDialog';

/**
 * Check if pickup and dropoff locations are the same
 * @param {Object} formData - Form data containing location information
 * @returns {boolean} - True if locations are the same
 */
const areLocationsSame = (formData) => {
    const { pickUpAddress, dropOffAddress, pickupLat, pickupLong, dropOffLat, dropOffLong } = formData;

    // If both have coordinates, compare them (most reliable)
    if (pickupLat && pickupLong && dropOffLat && dropOffLong) {
        // Check if coordinates are the same (within 0.0001 degree tolerance, ~11 meters)
        const latDiff = Math.abs(pickupLat - dropOffLat);
        const lngDiff = Math.abs(pickupLong - dropOffLong);
        const tolerance = 0.0001;
        
        if (latDiff < tolerance && lngDiff < tolerance) {
            return true;
        }
    }

    // Fallback: compare address strings (normalized)
    const normalizeAddress = (address) => {
        if (!address) return '';
        // Handle both string and object formats
        const addressStr = typeof address === 'string' ? address : (address.formatted_address || address.description || '');
        return addressStr.trim().toLowerCase().replace(/\s+/g, ' ');
    };

    const pickupNormalized = normalizeAddress(pickUpAddress);
    const dropoffNormalized = normalizeAddress(dropOffAddress);

    if (pickupNormalized && dropoffNormalized && pickupNormalized === dropoffNormalized) {
        return true;
    }

    return false;
};

/**
 * Custom hook for handling booking form submission
 */
export const useBookingSubmit = (formData, validateForm, setIsClicked, isDarkMode = false) => {
    const router = useRouter();
    const dispatch = useDispatch();
    const [showConfirmDialog, setShowConfirmDialog] = useState(false);

    // Extract form submission logic to be reused
    const proceedWithSubmission = useCallback(() => {
        
        // Save complete raw Form 1 data BEFORE any conversions
        const rawForm1Data = {
            pickUpAddress: formData.pickUpAddress,
            dropOffAddress: formData.dropOffAddress,
            tripType: formData.tripType === '1' ? 0 : 1,
            noOfPassengers: formData.noOfPassengers,
            departureDate: formData.departureDate,
            departureTime: formData.departureTime,
            returnDate: formData.returnDate,
            returnTime: formData.returnTime,
            pickupLat: formData.pickupLat,
            pickupLong: formData.pickupLong,
            dropOffLat: formData.dropOffLat,
            dropOffLong: formData.dropOffLong,
            gclid: formData.gclid,
            utm_source: formData.utm_source,
            utm_medium: formData.utm_medium,
            utm_campaign: formData.utm_campaign,
            utm_term: formData.utm_term,
            utm_content: formData.utm_content,
            formSource: formData.formSource,
            // Add timestamp to track when this data was captured
            capturedAt: new Date().toISOString(),
        };
        
        // Store form data in Redux
        // Convert 12-hour format to 24-hour format for storage
        
        const departureTime24h = moment(formData.departureTime, 'hh:mm A').format('HH:mm');
        const returnTime24h = moment(formData.returnTime, 'hh:mm A').format('HH:mm');
        

        const dateFormat = 'YYYY-MM-DD HH:mm';
        const pickupDate = moment.utc(formData.departureDate + ' ' + departureTime24h, dateFormat);
        const returnDate = moment.utc(formData.returnDate + ' ' + returnTime24h, dateFormat);



        const bookingData = {
            ...formData,
            departureUnix: pickupDate.unix(),
            departureString: pickupDate.format(dateFormat),
            returnUnix: returnDate.unix(),
            returnString: returnDate.format(dateFormat),
            // Store raw Form 1 data as object
            rawForm1Data: rawForm1Data,
        };

        // CRITICAL: Save to MULTIPLE storage locations for redundancy
        try {
            // Primary storage: sessionStorage (survives page refresh)
            sessionStorage.setItem('booking_form1', JSON.stringify(bookingData));
            
            // Secondary storage: Raw data backup (separate key for extra safety)
            sessionStorage.setItem('booking_raw_form1_backup', JSON.stringify(rawForm1Data));
            
        } catch (err) {
            console.error('[RAW_DATA/FORM1] ❌ CRITICAL: Error writing to sessionStorage:', err);
            // Try localStorage as emergency fallback
            try {
                localStorage.setItem('booking_raw_form1_emergency', JSON.stringify(rawForm1Data));
            } catch (localErr) {
                console.error('[RAW_DATA/FORM1] ❌ CRITICAL: All storage methods failed!', localErr);
            }
        }

        // Dispatch to redux
        dispatch(setBookingDetails(bookingData));

        // Send Google Tag Manager event after validation
        if (typeof window !== 'undefined' && window.gtag && typeof window.gtag === 'function') {
            try {
                const analytics = {
                    pickUpAddress: '',
                    dropOffAddress: '',
                    departureDate: '',
                    returnDate: '',
                    noOfPassengers: 0,
                    tripType: '',
                };
                
                const pickUpAddress = formData.pickUpAddress;
                const dropOffAddress = formData.dropOffAddress;
                const { departureDate, returnDate, noOfPassengers, tripType } = formData;
                
                analytics.pickUpAddress = pickUpAddress?.formatted_address || pickUpAddress || '';
                analytics.dropOffAddress = dropOffAddress?.formatted_address || dropOffAddress || '';
                analytics.departureDate = moment.utc(formData.departureDate + ' ' + departureTime24h, dateFormat);
                analytics.returnDate = moment.utc(formData.returnDate + ' ' + returnTime24h, dateFormat);
                analytics.noOfPassengers = parseInt(noOfPassengers) || 0;
                analytics.tripType = tripType === '1' ? 'One Way' : 'Return';

                
                window.gtag('event', 'TRIP_FORM_FILLED', {
                    event_category: 'Trip_form_filled',
                    event_label: 'trip_booking_form',
                    value: analytics,
                });

            } catch (trackingError) {
                console.error('❌ GTM tracking error:', trackingError);
                // Don't block form submission if tracking fails
            }
        } else {
            console.warn('⚠️ GTM not available - window.gtag not found');
        }

        // Redirect to booking information page
        router.push('/booking-information');
    }, [formData, dispatch, router]);

    const handleSubmit = useCallback((e) => {
        e.preventDefault();
        setIsClicked(true);

        const isFormValid = validateForm();

        if (isFormValid) {
            // Check if pickup and dropoff locations are the same
            if (areLocationsSame(formData)) {
                // Show confirmation dialog instead of proceeding
                setShowConfirmDialog(true);
                return;
            }
            
            // If locations are different, proceed directly
            proceedWithSubmission();
        }
    }, [formData, validateForm, setIsClicked, proceedWithSubmission]);

    const handleConfirm = useCallback(() => {
        setShowConfirmDialog(false);
        proceedWithSubmission();
    }, [proceedWithSubmission]);

    const handleCancel = useCallback(() => {
        setShowConfirmDialog(false);
    }, []);

    return {
        handleSubmit,
        confirmDialog: (
            <LocationConfirmDialog
                isOpen={showConfirmDialog}
                onClose={handleCancel}
                onConfirm={handleConfirm}
                isDarkMode={isDarkMode}
            />
        ),
    };
};
